import { Injectable } from '@angular/core';
import {
  EvRelayChargerStatus,
  EvSpaceChargerRowDto,
  EvSpaceChargerSpaceDto,
  EvSpaceChargerStatusDto,
  EvStationChargingStatus,
  EvStationSpaceCard,
  EvStationSpaceCardDto,
} from '../../models/cityparking/ev-station-monitor.model';

@Injectable({ providedIn: 'root' })
export class MapperEvStationMonitorService {
  mapFromDto(dto: EvStationSpaceCardDto): EvStationSpaceCard {
    const raw = dto as EvStationSpaceCardDto & Record<string, unknown>;
    return {
      gateId: dto.gateId ?? (raw['GateId'] as number) ?? 0,
      gateName: dto.gateName ?? (raw['GateName'] as string) ?? '',
      deviceKey: dto.deviceKey ?? (raw['DeviceKey'] as string) ?? '',
      siteId: dto.siteId ?? (raw['SiteId'] as number | null) ?? null,
      isFull: dto.isFull ?? (raw['IsFull'] as boolean) ?? false,
      transactionId: dto.transactionId ?? (raw['TransactionId'] as number | null) ?? null,
      transactionName: dto.transactionName ?? (raw['TransactionName'] as string) ?? '',
      licensePlate: dto.licensePlate ?? (raw['LicensePlate'] as string) ?? '',
      enterAt: dto.enterAt ? new Date(dto.enterAt) : (raw['EnterAt'] ? new Date(raw['EnterAt'] as string) : null),
      dwellTimeText: dto.dwellTimeText ?? (raw['DwellTimeText'] as string) ?? '',
      imageBase64: (dto.imageBase64
        ?? (raw['ImageBase64'] as string | undefined)
        ?? (raw['imageUrl'] as string | undefined)
        ?? (raw['pictureUrl'] as string | undefined)
        ?? '') as string,
      chargingStatus: this.normalizeChargingStatus(
        (dto.chargingStatus ?? raw['ChargingStatus']) as string | null | undefined,
      ),
      chargingStatusLabel: dto.chargingStatusLabel ?? (raw['ChargingStatusLabel'] as string) ?? '',
      historyUid: dto.historyUid ?? (raw['HistoryUid'] as string) ?? '',
      siteEv: dto.siteEv ?? (raw['SiteEv'] as string) ?? '',
      spaceCode: dto.spaceCode ?? (raw['SpaceCode'] as string) ?? dto.deviceKey ?? '',
      activate: dto.activate ?? (raw['Activate'] as boolean) ?? false,
      lineUid: dto.lineUid ?? (raw['LineUid'] as string) ?? '',
      chargerIsReady: dto.chargerIsReady ?? (raw['ChargerIsReady'] as boolean) ?? false,
      relayChargerStatus: null,
      relayChargerStatusLabel: '',
      relayChargerCode: '',
      relayChargedKwh: '',
      relayVendorErrorCode: '',
      relayChargerType: '',
    };
  }

  mapSpaceChargerStatusDto(dto: EvSpaceChargerStatusDto): EvSpaceChargerStatusDto {
    const raw = dto as EvSpaceChargerStatusDto & Record<string, unknown>;
    const spacesRaw = (dto.spaces ?? raw['Spaces'] ?? []) as EvSpaceChargerSpaceDto[];
    return {
      siteId: dto.siteId ?? (raw['SiteId'] as number) ?? 0,
      siteEv: dto.siteEv ?? (raw['SiteEv'] as string) ?? '',
      message: dto.message ?? (raw['Message'] as string | null) ?? null,
      spaces: spacesRaw.map((s) => this.mapSpaceRow(s)),
    };
  }

  /** Merge relay charger status onto cards by space_code ↔ deviceKey/spaceCode */
  mergeRelayStatusOntoCards(
    cards: EvStationSpaceCard[],
    relay: EvSpaceChargerStatusDto | null,
  ): EvStationSpaceCard[] {
    if (!relay?.spaces?.length) {
      return cards.map((c) => ({ ...c, relayChargerStatus: null }));
    }

    const bySpace = new Map<string, EvSpaceChargerSpaceDto>();
    for (const space of relay.spaces) {
      const key = (space.spaceCode || '').trim().toLowerCase();
      if (key) {
        bySpace.set(key, space);
      }
    }

    return cards.map((card) => {
      const match =
        bySpace.get((card.deviceKey || '').trim().toLowerCase())
        ?? bySpace.get((card.spaceCode || '').trim().toLowerCase())
        ?? bySpace.get((card.gateName || '').trim().toLowerCase());

      if (!match?.chargers?.length) {
        return { ...card, relayChargerStatus: null, relayChargerStatusLabel: '' };
      }

      const charger = this.pickPrimaryCharger(match.chargers);
      const status = this.normalizeRelayChargerStatus(charger.status);
      const vendorErrorCode = (charger.vendorErrorCode ?? '').trim();
      return {
        ...card,
        relayChargerStatus: status,
        relayChargerStatusLabel: this.relayChargerStatusLabelTh(
          status,
          vendorErrorCode,
          card.chargingStatus,
        ),
        relayChargerCode: charger.chargerCode || '',
        relayChargedKwh: charger.chargedKwh || '',
        relayVendorErrorCode: vendorErrorCode,
        relayChargerType: charger.chargerType || '',
      };
    });
  }

  toImageSrc(imageBase64: string | null | undefined): string | null {
    if (!imageBase64?.trim()) return null;
    const value = imageBase64.trim();
    if (value.startsWith('data:')) return value;
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `data:image/jpeg;base64,${value}`;
  }

  chargingStatusClass(status: EvStationChargingStatus): string {
    switch (status) {
      case 'waiting':
        return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
      case 'readyToCharge':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'charging':
        return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-slate-100 text-slate-500';
    }
  }

  chargingStatusLabelTh(
    status: EvStationChargingStatus | undefined,
    fallbackLabel?: string | null,
  ): string {
    switch (status) {
      case 'waiting':
        return 'รอชาร์จ';
      case 'readyToCharge':
        return 'พร้อมชาร์จ';
      case 'charging':
        return 'กำลังชาร์จ';
      default:
        return fallbackLabel?.trim() || '';
    }
  }

  /**
   * Visual class for relay charger pill.
   * กำลังชาร์จ (sky) only when relay `taken` AND chargingStatus === `charging`
   * vendorErrorCode `-1` → ชาร์จสำเร็จ (green)
   * vendorErrorCode `0x100202` → Offline (rose)
   */
  relayChargerStatusClass(
    status: EvRelayChargerStatus | null | undefined,
    vendorErrorCode?: string | null,
    chargingStatus?: EvStationChargingStatus | null,
  ): string {
    if (status === 'error' && this.isChargeCompletedVendorCode(vendorErrorCode)) {
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200/60';
    }
    if (this.isTrueOfflineError(status, vendorErrorCode)) {
      return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border border-rose-200/60';
    }
    if (this.isActivelyCharging(status, chargingStatus)) {
      return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 border border-sky-200/60';
    }
    switch (status) {
      case 'idle':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200/60';
      case 'taken':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200/60';
      case 'error':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200/60';
      default:
        return 'bg-slate-100 text-slate-500 border border-slate-200/60';
    }
  }

  /**
   * Relay labels:
   * `taken` + chargingStatus `charging` → กำลังชาร์จ
   * `taken` but not charging → use chargingStatus label (รอชาร์จ/พร้อมชาร์จ) or หัวถูกใช้
   * `-1` → ชาร์จสำเร็จ
   * `0x100202` → Offline
   */
  relayChargerStatusLabelTh(
    status: EvRelayChargerStatus | null | undefined,
    vendorErrorCode?: string | null,
    chargingStatus?: EvStationChargingStatus | null,
  ): string {
    switch (status) {
      case 'idle':
        return 'ยังไม่ชาร์จ';
      case 'taken':
        if (this.isActivelyCharging(status, chargingStatus)) return 'กำลังชาร์จ';
        return this.chargingStatusLabelTh(chargingStatus) || 'หัวถูกใช้';
      case 'error':
        if (this.isChargeCompletedVendorCode(vendorErrorCode)) return 'ชาร์จสำเร็จ';
        if (this.isOfflineVendorCode(vendorErrorCode)) return 'Offline';
        return 'Error';
      default:
        return '';
    }
  }

  /** True กำลังชาร์จ: relay taken + space chargingStatus === charging */
  isActivelyCharging(
    status: EvRelayChargerStatus | null | undefined,
    chargingStatus?: EvStationChargingStatus | null,
  ): boolean {
    return status === 'taken' && chargingStatus === 'charging';
  }

  /** Normal charge finished successfully. */
  isChargeCompletedVendorCode(vendorErrorCode?: string | null): boolean {
    return (vendorErrorCode ?? '').trim() === '-1';
  }

  /** True offline from vendor: 0x100202 */
  isOfflineVendorCode(vendorErrorCode?: string | null): boolean {
    return (vendorErrorCode ?? '').trim().toLowerCase() === '0x100202';
  }

  isTrueOfflineError(
    status: EvRelayChargerStatus | null | undefined,
    vendorErrorCode?: string | null,
  ): boolean {
    return status === 'error' && this.isOfflineVendorCode(vendorErrorCode);
  }

  /** ว่าง + หัวชาร์จ idle เท่านั้น (ห้าม offline / taken / ชาร์จสำเร็จ / ไม่มีสถานะ) */
  canCreateManualEntry(card: EvStationSpaceCard): boolean {
    return !card.isFull && card.relayChargerStatus === 'idle';
  }

  private mapSpaceRow(dto: EvSpaceChargerSpaceDto): EvSpaceChargerSpaceDto {
    const raw = dto as EvSpaceChargerSpaceDto & Record<string, unknown>;
    const chargersRaw = (dto.chargers ?? raw['Chargers'] ?? []) as EvSpaceChargerRowDto[];
    return {
      spaceCode: dto.spaceCode ?? (raw['SpaceCode'] as string) ?? '',
      spaceStatus: dto.spaceStatus ?? (raw['SpaceStatus'] as string) ?? '',
      chargers: chargersRaw.map((c) => this.mapChargerRow(c)),
    };
  }

  private mapChargerRow(dto: EvSpaceChargerRowDto): EvSpaceChargerRowDto {
    const raw = dto as EvSpaceChargerRowDto & Record<string, unknown>;
    const status = this.normalizeRelayChargerStatus(
      (dto.status ?? raw['Status']) as string | null | undefined,
    );
    return {
      chargerCode: dto.chargerCode ?? (raw['ChargerCode'] as string) ?? '',
      chargerType: dto.chargerType ?? (raw['ChargerType'] as string | null) ?? null,
      status,
      vendorErrorCode: dto.vendorErrorCode ?? (raw['VendorErrorCode'] as string | null) ?? null,
        statusLabel:
        dto.statusLabel
        ?? (raw['StatusLabel'] as string)
        ?? this.relayChargerStatusLabelTh(
          status,
          dto.vendorErrorCode ?? (raw['VendorErrorCode'] as string | null) ?? null,
          null,
        ),
      chargedKwh: dto.chargedKwh ?? (raw['ChargedKwh'] as string | null) ?? null,
    };
  }

  private pickPrimaryCharger(chargers: EvSpaceChargerRowDto[]): EvSpaceChargerRowDto {
    const taken = chargers.find((c) => this.normalizeRelayChargerStatus(c.status) === 'taken');
    if (taken) return taken;
    const error = chargers.find((c) => this.normalizeRelayChargerStatus(c.status) === 'error');
    if (error) return error;
    return chargers[0];
  }

  private normalizeRelayChargerStatus(value: string | null | undefined): EvRelayChargerStatus {
    const v = (value || '').trim().toLowerCase();
    if (v === 'idle' || v === 'taken') return v;
    return 'error';
  }

  private normalizeChargingStatus(value: string | null | undefined): EvStationChargingStatus {
    if (value === 'waiting' || value === 'readyToCharge' || value === 'charging') {
      return value;
    }
    return null;
  }
}
